import React, { useContext, useEffect, useState, useMemo } from 'react';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { APIContext } from '../contexts/APIContext';
import { SessionContext } from '../contexts/SessionContext';
import {
  capitalizeEveryWord,
  joinClassnames,
  previewString,
  removeMarkTag,
  trackUserClicks,
  validateStringForXSS,
  getValidToken,
} from '../utils';
import { Source } from '../types';
import { FullTranscriptModal } from './FullTranscriptModal';
import { Tooltip, TooltipProvider, TooltipTrigger } from './ui/Tooltip';
import { TooltipContent } from '@radix-ui/react-tooltip';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { useToast } from './ui/toast/UseToast';

interface SourceWithPublication extends Source {
  publication?: string;
}

export const enum BadgeType {
  PRIMARY = 'Primary',
  SECONDARY = 'Secondary',
}

// Source type (Primary vs Secondary) badge component
export const BadgeComponent = (props: any) => {
  const { badgeType } = props;
  let badgeText = '';
  switch (badgeType) {
    case BadgeType.PRIMARY:
      badgeText = 'Primary';
      break;
    case BadgeType.SECONDARY:
      badgeText = 'Secondary';
      break;
    default:
      break;
  }

  return (
    <p
      className={joinClassnames(
        badgeType === BadgeType.PRIMARY ? 'text-[#505050]' : '',
        badgeType === BadgeType.SECONDARY ? 'text-[#A100FF]' : '',
        'primary-badge text-[12px] leading-[normal] font-normal font-inter capitalize tracking-[0.28px] bg-[#E6DCFF] px-[8px] py-1 '
      )}
    >
      {badgeText}
    </p>
  );
};

export const ResponseDetails = (props: any) => {
  // Get the responseDetailsSources array and citation from the chatbot context
  const { responseDetailsSources, highScore, setHighScore } = useContext(ChatbotContext);
  // Get search progress state from API context
  const {
    searchResponses,
    searchBatches,
    batchType,
    WBUId,
    AppName,
    accounts,
    userDetails,
    chatSessionId,
    websocketConnectionRef: { current: websocket },
    matchingScore,
    setMatchingScore,
  } = useContext(APIContext);
  const { isPromptGenerating } = useContext(SessionContext);
  // Flag to show full transcript
  const [showFullTranscript, setShowFullTranscript] = useState(false);
  // Sets the current transccript, ciatation, and interview date
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [currentCitation, setCurrentCitation] = useState('');
  const [currentInterviewDate, setCurrentInterviewDate] = useState('');
  const [currentSorce, setCurrentSorce] = useState<string | undefined>('');
  const [topResponsesToggle, setTopResponsesToggle] = useState<boolean>(true);
  const { toast } = useToast();
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const transformedSearchData = useMemo(() => {
    if (!searchBatches || searchBatches.length === 0) return [];

    const allResults = searchBatches.flatMap((batch) => batch.results || []);

    return allResults.map(
      (item: any): SourceWithPublication => ({
        id: item.metadata?.interview_id || item.id,
        citation: item.metadata?.citation || '',
        interview_date: item.metadata?.interview_date || 'Unavailable',
        transcript: item.metadata?.interview_transcript || '',
        source: item.metadata?.source || 'Primary',
        score: item.score || 0,
        interviewId: item.metadata?.interview_id || item.id,
        publication: item.metadata?.publication || '',
      })
    );
  }, [searchBatches]);

  const topFiveSources = useMemo<SourceWithPublication[]>(() => {
    const sources = responseDetailsSources?.sourceList || [];
    return [...sources].sort((sourceA, sourceB) => sourceB.score - sourceA.score);
  }, [responseDetailsSources]);

  const allResponsesMerged = useMemo<SourceWithPublication[]>(() => {
    if (!transformedSearchData || transformedSearchData.length === 0) {
      return topFiveSources;
    }
    const topIds = new Set((topFiveSources || []).map((listId: any) => listId.id).filter(Boolean));
    const remaining = transformedSearchData.filter((allIds: any) => !topIds.has(allIds.id));

    return [...topFiveSources, ...remaining].sort((sourceA, sourceB) => sourceB.score - sourceA.score);
  }, [topFiveSources, transformedSearchData]);

  const displayData = useMemo(() => {
    if (!topResponsesToggle) {
      return {
        sourceList: allResponsesMerged,
        totalHits: searchResponses?.total_hits || allResponsesMerged.length,
      };
    }
    return {
      sourceList: topFiveSources,
      totalHits: topFiveSources.length,
    };
  }, [topResponsesToggle, allResponsesMerged, topFiveSources, searchResponses]);

  const openFullTranscript = (source: Source) => {
    setCurrentTranscript(source.transcript);
    setCurrentCitation(source.citation);
    setCurrentInterviewDate(source.interview_date || '');
    setCurrentSorce(source.source);
    // TODO: set the interview date once the BE sends it
    setShowFullTranscript(true);
    setMatchingScore(source.score.toFixed(2));
  };

  // useEffect to calculate and set the high score for the confidence score
  useEffect(() => {
    if (responseDetailsSources?.sourceList?.length > 0) {
      let sumOfScores = 0;
      const totalScripts = responseDetailsSources.sourceList.length;

      // Calculate Sum of individual confidence scores
      responseDetailsSources.sourceList.forEach((source) => {
        sumOfScores += source.score;
      });

      // Calculate the score using the formula
      const averageScore = sumOfScores / totalScripts;
      // eslint-disable-next-line prettier/prettier
      const tempHighScore = ((totalScripts / 5) * 0.25 + averageScore * 0.75) * 10;
      setHighScore(Math.round(tempHighScore));
    }
  }, [responseDetailsSources]);

  useEffect(() => {
    if (isPromptGenerating) {
      setTopResponsesToggle(true);
    }
  }, [isPromptGenerating]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
  }, [topResponsesToggle]);

  // Function to download all transcripts as a DOC file
  const downloadAllTranscripts = async () => {
    let content = `
  <html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>
  <head>
    <meta charset='utf-8'>
    <style>
      body {
        font-family: Aptos (Body);
        font-size: 12pt;
        line-height: 150%;
        color: #000;
      }
      p {
        margin: 4px 0;
      }
      hr {
        border-top: 10px solid #000;
      }
      .label {
        font-weight: bold;
      }
      .Transcript{
        line-height: 115%;
      }
    </style>
  </head>
  <body>
  `;

    const sortedResponses = [...allResponsesMerged];
    sortedResponses.forEach((source: SourceWithPublication, index: number) => {
      const cleanTranscript = removeMarkTag(source.transcript);
      const fullCitation =
        source.publication && source.publication.trim() ? `${source.citation}, ${source.publication}` : source.citation;
      const sanitizedCitation = validateStringForXSS(capitalizeEveryWord(fullCitation));
      content += `
      <p><span class="label">Response Details: ${source.source}</span></p>
      <p><span class="label">Matching Score: ${source.score.toFixed(2)}</span></p>
      <p><span class="label">Dated: ${source.interview_date}</span></p>
      <p><span class="label">Citation: ${sanitizedCitation}</span></p>
      <p><span class="label">Transcript</span></p>
      <p class="Transcript">${cleanTranscript}</p>
      <hr />
    `;
    });

    content += `</body></html>`;
    // Generate timestamp: YYYYMMDD_HHMMSS
    const now = new Date();
    const pad = (n: number) => n.toString().padStart(2, '0');
    const year = now.getFullYear();
    const month = pad(now.getMonth() + 1);
    const day = pad(now.getDate());
    const hours = pad(now.getHours());
    const minutes = pad(now.getMinutes());
    const seconds = pad(now.getSeconds());
    const timestamp = `${year}${month}${day}_${hours}${minutes}${seconds}`;
    const blob = new Blob(['\ufeff', content], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Transcripts_${timestamp}.doc`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast({
      status: 'success',
      description: 'Transcripts are downloaded successfully',
    });
    const token = await getValidToken();
    trackUserClicks(websocket, 'DownloadMatchedResponse', userDetails, chatSessionId, WBUId, AppName, accounts, token);
  };

  return (
    <>
      {/* If there are responseDetailsSources, display them and if not, then show blank message */}
      {responseDetailsSources?.showCitation === true && batchType == 'batch' ? (
        <div className="flex items-center justify-center py-4  h-[75vh]">
          <img src="/icons/loader-ring.svg" alt="Loading..." className="w-8 h-8 animate-spin" />
        </div>
      ) : displayData.sourceList?.length > 0 &&
        responseDetailsSources?.showCitation === true &&
        batchType == 'complete' ? (
        <>
          <div className="c-matched-transcripts">
            <div className="c-matched-transcripts__wrapper">
              <div className="c-matched-transcripts__inner-wrap">
                <div className="c-matched-transcripts__tgl-wrap">
                  <div className="sa-toggle-btn">
                    <input
                      type="checkbox"
                      id="toggle-one"
                      className="sa-toggle-btn__input"
                      checked={topResponsesToggle}
                      onChange={(e) => {
                        setTopResponsesToggle(e.target.checked);
                        (async () => {
                          const token = await getValidToken();
                          trackUserClicks(
                            websocket,
                            'AllMatchedResponse',
                            userDetails,
                            chatSessionId,
                            WBUId,
                            AppName,
                            accounts,
                            token
                          );
                        })();
                      }}
                    />

                    <label htmlFor="toggle-one" className="sa-toggle-btn__label">
                      <span className="sa-toggle-btn__checkbox "></span>
                      <span className="sa-toggle-btn__label-single ">All Matched Responses</span>
                    </label>
                  </div>{' '}
                  <button
                    className="c-matched-transcripts__btn"
                    role="button"
                    aria-label="Download"
                    data-tooltip-title="Download Responses"
                    data-tooltip-toggle="tooltip"
                    data-custclass="-uploadTltip"
                    onClick={downloadAllTranscripts}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M14.6676 11.6302V12.7387C14.6651 12.9956 14.6115 13.2495 14.5105 13.4857C14.4094 13.722 14.2626 13.9359 14.0785 14.1151C13.8944 14.2943 13.6769 14.4353 13.438 14.5299C13.1991 14.6246 12.9438 14.671 12.6869 14.6665H3.26614C2.75466 14.6657 2.26408 14.4624 1.90197 14.1012C1.53985 13.74 1.33556 13.2501 1.3335 12.7387V11.6302C1.33625 11.5552 1.36789 11.4842 1.42195 11.432C1.47601 11.3799 1.54817 11.3508 1.62326 11.3508C1.69835 11.3508 1.77051 11.3799 1.82457 11.432C1.87863 11.4842 1.91065 11.5552 1.9134 11.6302V12.7387C1.92139 13.0946 2.06624 13.4338 2.31831 13.6852C2.57039 13.9367 2.91014 14.0812 3.26614 14.0883H12.6869C13.0505 14.0911 13.4007 13.9513 13.6626 13.6991C13.9244 13.4468 14.0769 13.1021 14.0877 12.7387V11.6302C14.0904 11.5552 14.1221 11.4842 14.1761 11.432C14.2302 11.3799 14.3023 11.3508 14.3774 11.3508C14.4525 11.3508 14.5247 11.3799 14.5787 11.432C14.6328 11.4842 14.6648 11.5552 14.6676 11.6302ZM7.81467 11.7494C7.86074 11.7954 7.90945 11.7969 7.9973 11.7969H8.00073H8.00835C8.07635 11.8041 8.14474 11.787 8.20128 11.7486L12.2561 7.698C12.3033 7.6355 12.3289 7.55922 12.3289 7.48087C12.3289 7.40252 12.3033 7.32634 12.2561 7.26383C12.2026 7.21726 12.1344 7.19158 12.0635 7.19158C11.9926 7.19158 11.9241 7.21726 11.8706 7.26383L8.27867 10.8525V1.62107C8.28007 1.58382 8.27404 1.54666 8.26075 1.51183C8.24746 1.47701 8.22714 1.44527 8.20128 1.41842C8.17542 1.39157 8.14446 1.3702 8.11015 1.35561C8.07585 1.34102 8.03877 1.3335 8.00149 1.3335C7.96421 1.3335 7.92752 1.34102 7.89321 1.35561C7.85891 1.3702 7.82795 1.39157 7.80209 1.41842C7.77623 1.44527 7.7559 1.47701 7.74261 1.51183C7.72932 1.54666 7.72329 1.58382 7.72469 1.62107V10.8369L4.14763 7.26383C4.09411 7.21723 4.02568 7.19149 3.95471 7.19149C3.88374 7.19149 3.81493 7.21723 3.76141 7.26383C3.7275 7.28896 3.69995 7.32164 3.68096 7.35934C3.66198 7.39704 3.65199 7.43866 3.65199 7.48087C3.65199 7.52308 3.66198 7.5647 3.68096 7.6024C3.69995 7.6401 3.7275 7.67287 3.76141 7.698L7.81467 11.7494Z"
                        fill="black"
                      ></path>
                    </svg>
                  </button>
                </div>
                <div className="c-matched-transcripts__responses !max-h-[calc(100vh-148px)]" ref={scrollContainerRef}>
                  <div className="c-matched-transcripts__responses-innerbody">
                    <span className="c-matched-transcripts__title flex">
                      {!topResponsesToggle
                        ? `Showing ${allResponsesMerged.length} responses`
                        : `Showing Top ${displayData.sourceList.length} Responses of ${allResponsesMerged.length}`}
                    </span>
                    {topResponsesToggle ? (
                      <>
                        <div className="c-matched-transcripts__score-wrapper">
                          <span className="c-matched-transcripts__score">Confidence Score</span>
                          <button className="c-matched-transcripts__score-btn">
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger>
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M9.99996 20C7.34945 19.9946 4.80903 18.9393 2.93484 17.0651C1.06064 15.1909 0.00535544 12.6505 0 10C0.00535544 7.34949 1.06064 4.80907 2.93484 2.93487C4.80903 1.06068 7.34945 0.00539119 9.99996 3.57494e-05C11.3142 -0.00348288 12.6161 0.252773 13.8309 0.754072C15.0458 1.25537 16.1496 1.99183 17.0789 2.92111C18.0081 3.85039 18.7446 4.95418 19.2459 6.16902C19.7472 7.38386 20.0034 8.6858 19.9999 10C20.0034 11.3142 19.7472 12.6161 19.2459 13.831C18.7446 15.0458 18.0081 16.1496 17.0789 17.0789C16.1496 18.0082 15.0458 18.7446 13.8309 19.2459C12.6161 19.7472 11.3142 20.0035 9.99996 20ZM9.99996 0.869367C7.57902 0.871485 5.25784 1.83414 3.54597 3.54601C1.8341 5.25787 0.871449 7.57906 0.86933 10C0.883743 12.4171 1.85035 14.7312 3.55956 16.4404C5.26877 18.1496 7.58282 19.1162 9.99996 19.1306C12.4209 19.1285 14.7421 18.1659 16.454 16.454C18.1658 14.7421 19.1285 12.4209 19.1306 10C19.1357 8.79953 18.903 7.60993 18.4459 6.49986C17.9889 5.38978 17.3165 4.38121 16.4676 3.53234C15.6188 2.68347 14.6102 2.0111 13.5001 1.55405C12.39 1.09699 11.2004 0.864286 9.99996 0.869367Z"
                                      fill="black"
                                    ></path>
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M12.6288 16.5335H7.37121C7.26229 16.5308 7.15801 16.4888 7.07757 16.4153C6.99712 16.3418 6.94591 16.2418 6.93335 16.1335C6.94591 16.0253 6.99712 15.9252 7.07757 15.8517C7.15801 15.7782 7.26229 15.7363 7.37121 15.7335H12.6283C12.6838 15.7286 12.7398 15.7352 12.7927 15.7531C12.8456 15.7709 12.8942 15.7995 12.9354 15.8371C12.9766 15.8748 13.0096 15.9205 13.0322 15.9715C13.0548 16.0226 13.0665 16.0777 13.0667 16.1335C13.0665 16.1893 13.0548 16.2444 13.0322 16.2954C13.0096 16.3463 12.9767 16.3921 12.9356 16.4297C12.8944 16.4673 12.8459 16.4959 12.7931 16.5138C12.7403 16.5317 12.6843 16.5384 12.6288 16.5335Z"
                                      fill="black"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M9.95579 16.0001C9.84165 15.9935 9.73377 15.9458 9.65209 15.8658C9.57042 15.7858 9.52049 15.6789 9.51153 15.5649V8.60388H8.1782C8.06396 8.5974 7.95595 8.54973 7.87417 8.46972C7.79238 8.3897 7.74237 8.28275 7.7334 8.16869C7.74236 8.05471 7.79229 7.94783 7.87397 7.86782C7.95564 7.78782 8.06352 7.7401 8.17766 7.73349H9.95526C10.0136 7.73043 10.072 7.73953 10.1267 7.7602C10.1814 7.78086 10.2312 7.81266 10.2729 7.85356C10.3147 7.89447 10.3475 7.9436 10.3693 7.99784C10.3911 8.05208 10.4014 8.11026 10.3995 8.16869V15.5649C10.4014 15.6233 10.3911 15.6814 10.3693 15.7356C10.3476 15.7898 10.3148 15.8389 10.2731 15.8798C10.2314 15.9207 10.1817 15.9525 10.1271 15.9732C10.0724 15.994 10.0141 16.0031 9.95579 16.0001Z"
                                      fill="black"
                                    />
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M8.53345 4.80022C8.55252 4.57092 8.66118 4.35836 8.83588 4.20862C9.01058 4.05887 9.23725 3.984 9.46678 4.00023C9.6963 3.984 9.92297 4.05887 10.0977 4.20862C10.2724 4.35836 10.381 4.57092 10.4001 4.80022C10.381 5.02953 10.2724 5.24208 10.0977 5.39183C9.92297 5.54157 9.6963 5.61645 9.46678 5.60022C9.23725 5.61645 9.01058 5.54157 8.83588 5.39183C8.66118 5.24208 8.55252 5.02953 8.53345 4.80022Z"
                                      fill="black"
                                    />
                                  </svg>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="bottom"
                                  className="bg-[#505050] border-0 max-w-[218px] drop-shadow-[0_12px_16px_rgba(0,0,0,0.12)] p-2"
                                >
                                  <p className="tooltip-content text-white text-base font-normal font-inter leading-[150%] tracking-[-0.2px] not-italic">
                                    The confidence score is based on the relevance of the transcript to the user prompt
                                    and the quantity of transcripts returned.
                                  </p>
                                  <TooltipPrimitive.Arrow width={11} height={5} style={{ fill: '#505050' }} />
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </button>
                        </div>
                        <span className="c-matched-transcripts__score-no flex">{highScore + '/10'}</span>
                      </>
                    ) : (
                      <></>
                    )}
                    {displayData.sourceList.map((source: SourceWithPublication, index) => (
                      <div key={index} className="c-matched-transcripts-transcript">
                        <div className="c-matched-transcripts__citation-wrap">
                          <div className="c-matched-transcripts__citation-title-wrap">
                            <span className="c-matched-transcripts__citation-title">
                              Citation <span className="c-matched-transcripts__citation-score">{index + 1}</span>
                            </span>
                            <span
                              className="c-matched-transcripts__citation-sub-title flex text-left break-words"
                              dangerouslySetInnerHTML={{
                                __html: validateStringForXSS(
                                  capitalizeEveryWord(
                                    source.publication && source.publication.trim()
                                      ? `${source.citation}, ${source.publication}`
                                      : source.citation
                                  )
                                ),
                              }}
                            ></span>
                          </div>
                        </div>
                        <div className="c-matched-transcripts__date-wrap">
                          <span className="c-matched-transcripts__date-heading flex">Dated</span>
                          <span className="c-matched-transcripts__date flex">{source.interview_date}</span>
                        </div>
                        <div className="c-matched-transcripts__preview">
                          <span className="c-matched-transcripts__preview-heading flex">Transcript Preview</span>
                          <div
                            className="c-matched-transcripts__preview-cont text-left break-words"
                            dangerouslySetInnerHTML={{
                              __html: validateStringForXSS(previewString(removeMarkTag(source.transcript))),
                            }}
                          >
                            {/* {previewString(source.transcript)} */}
                          </div>
                        </div>
                        <div className="c-matched-transcripts__preview-btns">
                          <div className="c-matched-transcripts__preview-btns-wrap">
                            {/* <span className="c-matched-transcripts__preview-primary">Primary</span>
        <span className="c-matched-transcripts__preview-core">Core</span> */}
                            <BadgeComponent badgeType={source.source} />
                          </div>
                          <a
                            href="javascript:void(0);"
                            className="c-matched-transcripts__preview-link"
                            onClick={() => openFullTranscript(source)}
                          >
                            View full transcript
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <FullTranscriptModal
            showFullTranscript={showFullTranscript}
            setShowFullTranscript={setShowFullTranscript}
            currentCitation={currentCitation}
            currentInterviewDate={currentInterviewDate}
            currentTranscript={currentTranscript}
            currentSorce={currentSorce}
            matchingScore={matchingScore}
          />
        </>
      ) : (
        <div
          className={`no-responseDetailsSources-container flex flex-col w-full items-center justify-center gap-6 h-[75vh] ${
            !props.showRightPanel ? 'hidden' : ''
          }`}
        >
          {/* <div className="items-center justify-center w-[100px] h-[100px]">
            <img src="/icons/bot-new.svg" className="w-80 h-87"></img>
          </div> */}
          <div className="text-wrapper flex flex-col">
            {/* <p className="text-base leading-[16px] font-normal text-black text-opacity-[0.87] text-center font-graphik">
              Response details are empty
            </p> */}
            <p className="text-[15px] font-normal leading-[17px] text-[#505050] text-center font-graphik w-[222px]">
              Ask a prompt first so you can see the response details populated from our CORE database.
            </p>
          </div>
        </div>
      )}
    </>
  );
};
